import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import { useDefaultMessages } from './useDefaultMessages';
import { ResumeLink } from './components/ResumeLink';
import { EmailLink } from './components/EmailLink';
import { enrichMarkdown, SmartLink } from './components/SmartLinks';
import Loading from './components/Loading';

const markdownComponents = {
  a: ({ node, ...props }: any) => <SmartLink {...props} />,
};

function StreamingText({ text, scrollRef, promptId }: { text: string; scrollRef: React.RefObject<HTMLDivElement | null>; promptId?: string }) {
  const [displayed, setDisplayed] = useState('');
  const endRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setDisplayed('');
    let i = 0;
    const chunkSize = Math.max(1, Math.ceil(text.length / 80));
    const interval = setInterval(() => {
      i += chunkSize;
      setDisplayed(text.slice(0, i));
      if (i >= text.length) clearInterval(interval);
    }, 10);
    return () => clearInterval(interval);
  }, [text]);

  const lastScrollTime = useRef(0);

  useEffect(() => {
    const now = Date.now();
    if (now - lastScrollTime.current < 200) return;

    if (endRef.current && scrollRef.current && promptId) {
      const container = scrollRef.current;
      const promptEl = document.getElementById(promptId);
      if (!promptEl) return;

      const promptTop = promptEl.getBoundingClientRect().top;
      const containerTop = container.getBoundingClientRect().top;

      // Don't scroll if the prompt bubble is already above the viewport
      if (promptTop < containerTop) return;

      const cursorBottom = endRef.current.getBoundingClientRect().bottom;
      const containerBottom = container.getBoundingClientRect().bottom;

      if (cursorBottom > containerBottom) {
        const scrollAmount = Math.min(
          cursorBottom - containerBottom + 16,
          promptTop - containerTop
        );
        container.scrollTo({
          top: container.scrollTop + scrollAmount,
          behavior: 'smooth',
        });
        lastScrollTime.current = now;
      }
    }
  }, [displayed, scrollRef, promptId]);

  const isStreaming = displayed.length < text.length;

  return <><Markdown components={markdownComponents}>{enrichMarkdown(displayed)}</Markdown>{isStreaming && <span ref={endRef} className="streaming-cursor">&#9646;</span>}</>;
}

const BOT_NAME = 'VincentBot';

const BORDER_RADIUS = 100;
export const GRAY_100 = 'var(--gray-100)';
export const GRAY_400 = 'var(--gray-400)';
export const GRAY_600 = 'var(--gray-600)';

function PromptBubble({ prompt }: { prompt: string | ReactElement }) {
  return (
    <p
      style={{
        borderRadius: 35,
        backgroundColor: 'var(--accent)',
        padding: '8px 20px',
        alignSelf: 'flex-end',
        maxWidth: '80%',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {prompt}
    </p>
  );
}

function BotResponse({
  name = BOT_NAME,
  response,
  stream = false,
  scrollRef,
  promptId,
}: {
  name?: String;
  response: string | ReactElement;
  stream?: boolean;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  promptId?: string;
}) {
  return (
    <div>
      <i style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: -14, display: 'block' }}>{name}</i>
      <div>{stream && typeof response === 'string' && scrollRef ? <StreamingText text={response} scrollRef={scrollRef} promptId={promptId} /> : typeof response === 'string' ? <Markdown components={markdownComponents}>{enrichMarkdown(response)}</Markdown> : response}</div>
    </div>
  );
}

type MessageType = 'prompt' | 'response';
export type Message = {
  type: MessageType;
  content: string | ReactElement;
  stream?: boolean;
  id?: string;
};

function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const chatHistoryRef = useRef<{role: string, content: string}[]>([]);
  const [prompt, setPrompt] = useState<string>('');

  const [error, setError] = useState<string | undefined>();
  const [loading, setIsLoading] = useState<boolean>(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const addMessage = useCallback(
    (message: Message) =>
      setMessages((existingMessages: Message[]) => [
        ...existingMessages,
        message,
      ]),
    []
  );

  const lastPromptIdRef = useRef<string | null>(null);
  const shouldScrollRef = useRef(false);

  useEffect(() => {
    if (shouldScrollRef.current && lastPromptIdRef.current) {
      // Small delay to let the DOM render the new message
      setTimeout(() => {
        const el = document.getElementById(lastPromptIdRef.current!);
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        shouldScrollRef.current = false;
      }, 50);
    }
  }, [messages]);


  const handleSendPrompt = useCallback(
    async (prompt: string): Promise<string> => {
      setError(undefined);
      setIsLoading(true);
      const id = `msg-${Date.now()}`;
      lastPromptIdRef.current = id;
      addMessage({
        type: 'prompt',
        content: prompt,
        id,
      });
      setPrompt('');
      // Scroll to bottom so user sees their message + loading
      setTimeout(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
      }, 50);

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt, history: chatHistoryRef.current }),
      });

      setIsLoading(false)

      if (!response.ok) {
        setError(`Oh no! An error occurred 😿 ${response.statusText} `)
        setPrompt(prompt);
      } else {
        const data = await response.json();
        chatHistoryRef.current = [
          ...chatHistoryRef.current,
          { role: 'user', content: prompt },
          { role: 'assistant', content: data.response },
        ];
        shouldScrollRef.current = true;
        addMessage({
          type: 'response',
          content: data.response,
          stream: true,
        });
      }
      return id;
    },
    [addMessage]
  );

  const defaultMessages = useDefaultMessages(handleSendPrompt);

  type ThemeMode = 'auto' | 'light' | 'dark';
  const [themeMode, setThemeMode] = useState<ThemeMode>(() =>
    (localStorage.getItem('theme') as ThemeMode) || 'auto'
  );

  const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const isDark = themeMode === 'auto' ? systemDark : themeMode === 'dark';

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    localStorage.setItem('theme', themeMode);
  }, [themeMode, isDark]);

  const cycleTheme = () => {
    setThemeMode(prev => prev === 'auto' ? 'light' : prev === 'light' ? 'dark' : 'auto');
  };

  return (
    <div className="App" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="theme-toggle" style={{ position: 'fixed', top: 16, right: 16, zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <button
        aria-label="Toggle theme"
        onClick={cycleTheme}
        style={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          border: 'none',
          backgroundColor: 'var(--gray-100)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        {isDark ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
        )}
      </button>
      <span className="theme-auto-label" style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2 }}>{themeMode}</span>
      </div>
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
        }}
      >
        <div className="app-content" style={{ maxWidth: 1000, margin: '0 auto', padding: '40px 15px 20px', display: 'flex', flexDirection: 'column' }}>
          <h1 className="app-title" style={{ fontFamily: "'Merriweather', serif", fontWeight: 400, fontSize: 54, letterSpacing: 3 }}>
            Vincent Liaw
          </h1>
          {[...defaultMessages, ...messages].map(({ type, content, stream, id }, i) => {
            if (type === 'prompt') {
              return <div key={i} id={id} style={{ display: 'flex', flexDirection: 'column' }}><PromptBubble prompt={content} /></div>;
            } else return <BotResponse key={i} response={content} stream={stream} scrollRef={scrollRef} promptId={lastPromptIdRef.current ?? undefined} />;
          })}
          {loading && <Loading />}
          {Boolean(error?.length) && (
          <div>
            <span style={{ color: 'var(--error)'}}>{error}</span>
            <span
              style={{
                color: GRAY_400,
                cursor: 'pointer',
                borderBottom: '1px solid',
                fontWeight: 'bold',
              }}
              tabIndex={0}
              onClick={() => handleSendPrompt(prompt)
              }
            >Try again
            </span>
            </div>)}
        </div>
      </div>

      <div style={{ flexShrink: 0, paddingBottom: 12, paddingTop: 8 }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 15px' }}>
          <form
            onSubmit={(e) => e.preventDefault()}
            style={{
              display: 'flex',
              position: 'relative',
            }}
          >
            <textarea
              autoFocus
              placeholder={`Message ${BOT_NAME}`}
              rows={1}
              style={{
                borderRadius: 35,
                backgroundColor: GRAY_100,
                color: 'var(--text)',
                padding: '14px 45px 14px 20px',
                fontStyle: 'italic',
                letterSpacing: 0.5,
                outline: 'none',
                border: 'none',
                width: '100%',
                fontFamily: 'inherit',
                fontSize: 'inherit',
                resize: 'none',
                lineHeight: '20px',
                height: 48,
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
              value={prompt}
              onChange={(e) => {
                setPrompt(e.target.value);
                e.target.style.height = '48px';
                if (e.target.scrollHeight > 48) {
                  e.target.style.height = e.target.scrollHeight + 'px';
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && prompt.length) {
                  e.preventDefault();
                  const target = e.target as HTMLTextAreaElement;
                  handleSendPrompt(prompt);
                  setPrompt('');
                  target.style.height = '48px';
                }
              }}
            />
            <button
              aria-label="Send"
              style={{
                position: 'absolute',
                right: 7,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 36,
                height: 36,
                padding: 0,
                borderRadius: '50%',
                backgroundColor: 'var(--accent)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              disabled={!Boolean(prompt.length)}
              onClick={() => handleSendPrompt(prompt)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          </form>
          <p style={{ textAlign: 'center', color: GRAY_400, fontSize: 14, margin: '8px 0 0' }}>
            This is a bot, and it can make mistakes. To talk to a real human
            (Vincent), download my <ResumeLink text="resume" /> or reach out via{' '}
            <EmailLink text="email" />
          </p>
        </div>
      </div>
    </div>
  );
}

export default App;
